import * as _ from 'lodash';

export const _set = _.set;
export const _unset = _.unset;
export const _get = _.get;
export const _chunk = _.chunk;
export const _isEmpty = _.isEmpty;
export const _isPlainObject = _.isPlainObject;
export const _size = _.size;
export const _keys = _.keys;
export const _cloneDeep = _.cloneDeep;
export const _merge = _.merge;
export const _omit = _.omit;
export const _invert = _.invert;
export const _entries = _.entries;
export const _mapKeys = _.mapKeys;
export const _cloneDeepWith = _.cloneDeepWith;
export const _pick = _.pick;
export const _mapValues = _.mapValues;
export const _values = _.values;
export const _isEqual = _.isEqual;

// Or make imports from lodash/fp work in esm
export const fpSet = _.curry((path: string, value: any, object: any) => {
  return _.set(_.cloneDeep(object), path, value);
});

export function deepMergeBasic<T, X>(t: T, x: X): T & X {
  return _.mergeWith(_.cloneDeep(t), _.cloneDeep(x), (objValue, srcValue) => {
    return _.isArray(objValue)
      ? objValue.concat(srcValue)
      : undefined;
  });
}

export function mapObjectDeepInverse(
  obj: any,
  mapper: (value: any, key: string, path: string[]) => any,
): any {
  function _map(
    obj: any,
    mapper: (value: any, key: string, path: string[]) => any,
    currentPath: string[],
  ): any {
    // Apply the mapper function first at the current level
    let mappedValue = mapper(obj, currentPath[currentPath.length - 1], currentPath);

    if (typeof mappedValue === 'object' && mappedValue !== null) {
      // If the mapper hasn't transformed the current obj (i.e., it still is an object),
      // we continue iterating over its properties
      for (let key in mappedValue) {
        if (mappedValue.hasOwnProperty(key)) {
          let newPath = currentPath.concat(key);
          mappedValue[key] = _map(mappedValue[key], mapper, newPath);
        }
      }
    }

    return mappedValue;
  }
  return _map(obj, mapper, []);
}

export async function mapObjectDeepInverseAsync(
  obj: any,
  mapper: (
    value: any,
    key: string,
    path: string[],
  ) =>
    | { map?: '__keepOriginal' | any; continue?: boolean }
    | Promise<{ map?: '__keepOriginal' | any; continue?: boolean }>,
) {
  async function _map(
    obj: any,
    mapper: (
      value: any,
      key: string,
      path: string[],
    ) =>
      | { map?: '__keepOriginal' | any; continue?: boolean }
      | Promise<{ map?: '__keepOriginal' | any; continue?: boolean }>,
    currentPath: string[],
  ) {
    // Apply the mapper function first at the current level
    const mappedResult = await mapper(obj, currentPath[currentPath.length - 1], currentPath);

    const shouldMap = mappedResult.map !== '__keepOriginal';
    const shouldContinue = mappedResult.continue !== false;
    const mappedValue = shouldMap ? mappedResult.map : obj;

    // If we shouldn't continue, or if the mapped value is not an object we can iterate over,
    // then we should just return the mapped value and stop the recursion for this branch.
    if (!shouldContinue || typeof mappedValue !== 'object' || mappedValue === null) {
      return mappedValue;
    }

    // We also can't iterate if the original value wasn't an object.
    if (typeof obj !== 'object' || obj === null) {
      return mappedValue;
    }

    let hasChanged = shouldMap;
    // If the object was mapped, use the mapped value as the base. Otherwise, create a new object/array.
    const newObj = shouldMap ? mappedValue : Array.isArray(obj) ? [] : {};

    // Iterate over the keys of the *original* object to map its children.
    for (const key in obj) {
      if (Object.prototype.hasOwnProperty.call(obj, key)) {
        const oldChild = obj[key];
        const newChild = await _map(oldChild, mapper, currentPath.concat(key));
        newObj[key] = newChild;
        // Track if any child has changed to decide whether to return the new object or the original.
        if (newChild !== oldChild) {
          hasChanged = true;
        }
      }
    }

    // Only return the new object if something has actually changed. Otherwise, return the original.
    return hasChanged ? newObj : obj;
  }

  const result = await _map(obj, mapper, []);
  return result;
}

export function omitDeep(collection: object, excludeKeys: string[]) {
  function omitFn(value) {
    if (value && typeof value === 'object') {
      excludeKeys.forEach((key) => {
        delete value[key];
      });
    }
  }

  return _cloneDeepWith(collection, omitFn);
}

export function stripEmptyDeep(obj: any, isRoot = true): any {
  function isEmpty(value: any): boolean {
    return (
      value === null
      || value === undefined
      || value === ''
      || (_.isArray(value) && value.length === 0)
      || (_.isPlainObject(value) && Object.keys(value).length === 0)
    );
  }
  // Handle arrays
  if (_.isArray(obj)) {
    const filtered = obj
      .map((item) => stripEmptyDeep(item, false))
      .filter((item) => !isEmpty(item));

    // For root level, always return an array
    return isRoot ? filtered : (filtered.length === 0 ? undefined : filtered);
  }

  // Handle objects
  if (_.isPlainObject(obj)) {
    const result = {};

    for (const key in obj) {
      const value = stripEmptyDeep(obj[key], false);

      if (!isEmpty(value)) {
        result[key] = value;
      }
    }

    // For root level, always return an object
    return isRoot ? result : (Object.keys(result).length === 0 ? undefined : result);
  }

  // For primitive values
  return obj;
}
